import { Experience, getFamilyId } from "types/experience";
import { isExperienceItem } from "types/experience-view";
import type { ExperienceViewItem } from "types/experience-view";

/**
 * A copy's type is its own, so a family can mix bookable and information-only variants on one
 * date. Something the guest can book wins, then a message, then a variant with nothing on the date.
 */
const variantRank = (experience: Experience) =>
    experience.unavailable ? 2 : experience.informationOnly ? 1 : 0;

/**
 * The variant to show for an experience and its copies on the date: the best by `variantRank`,
 * and among equals the first, which `filterAndSortExperiences` has already ordered by sortOrder.
 */
function findFamilyVariant(
    experiences: Experience[],
    experienceId: number
): Experience | undefined {
    return experiences
        .filter(
            (experience) => experience.id === experienceId || experience.parentId === experienceId
        )
        .reduce<
            Experience | undefined
        >((best, experience) => (!best || variantRank(experience) < variantRank(best) ? experience : best), undefined);
}

/**
 * A parent and its copies are one experience to the guest, represented by the parent (BOOK-3435).
 * availableTimes returns each variant separately, so a family with rules on both rows renders twice
 * unless it is collapsed here. The family keeps its first row's place and shows its best variant.
 */
function dedupeByFamily(experiences: Experience[]): Experience[] {
    const seen = new Set<number>();

    return experiences.flatMap((experience) => {
        const familyId = getFamilyId(experience);
        if (seen.has(familyId)) return [];
        seen.add(familyId);
        return [findFamilyVariant(experiences, familyId) ?? experience];
    });
}

/**
 * The families the catalogue offers, for filtering availableTimes rows down to what the guest can
 * actually reach. availableTimes accepts a variant on its own status, so a live copy under a parent
 * that is not live comes back from it while the family is absent from the catalogue (BOOK-3910).
 *
 * Null while the catalogue is unavailable — loading, or failed. Callers must not filter then:
 * an empty list is a worse answer than a list carrying a stale row for a moment.
 */
function getCatalogueFamilyIds(items: ExperienceViewItem[] | undefined): Set<number> | null {
    if (!items) return null;

    return new Set(items.filter(isExperienceItem).map((item) => item.id));
}

export { dedupeByFamily, findFamilyVariant, getCatalogueFamilyIds };
